import type { ReactNode } from "react";
import { useRouterState } from "@tanstack/react-router";
import { SidebarNavigationSimple } from "@/components/application/app-navigation/sidebar-navigation/sidebar-simple";
import { NavAccountCard } from "@/components/application/app-navigation/base-components/nav-account-card";
import type { NavItemType } from "@/components/application/app-navigation/config";
import { AppHeader } from "@/components/app/AppHeader";
import { VersionNoticeDialog } from "@/components/app/VersionNoticeDialog";
import { useAccountMenu } from "@/components/app/accountMenu";
import { ettsBootstrap } from "@/lib/bootstrap";

/**
 * AppShell — sjednocený layout admin SPA: sidebar s navigací (`SidebarNavigationSimple`,
 * aktivní stav přes `activeUrl === href`) a nad obsahem horní utilitní lišta.
 *
 * Ovládání účtu žije v kartě v patičce sidebaru; horní lišta otevírá tytéž položky
 * pod avatarem (viz `useAccountMenu`). Odkazy dělají SPA navigaci přes react-aria
 * RouterProvider (main.tsx) napojený na TanStack Router.
 */
export interface AppShellProps {
  items: NavItemType[];
  children: ReactNode;
}

export function AppShell({ items, children }: AppShellProps) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const boot = ettsBootstrap();
  const { display, menuTop, menuItems, onSignOut } = useAccountMenu();

  // Verze pod kartou účtu, ne nad ní — proto uvnitř `accountCard`, který sidebar
  // renderuje jako poslední blok patičky.
  const accountCard = (
    <div className="flex flex-col gap-2">
      {display && (
        <NavAccountCard
          title={display.title}
          subtitle={display.subtitle}
          initials={display.initials}
          menuTop={menuTop}
          menuItems={menuItems}
          onSignOut={onSignOut}
        />
      )}
      <p className="truncate px-1 text-xs text-quaternary">
        {boot.version} · {boot.buildProfile}
      </p>
    </div>
  );

  return (
    <div className="flex min-h-screen flex-col bg-primary lg:flex-row">
      <SidebarNavigationSimple activeUrl={pathname} items={items} accountCard={accountCard} />
      <div className="flex min-w-0 flex-1 flex-col">
        <AppHeader />
        <main className="min-w-0 flex-1 bg-primary p-4 lg:p-8">{children}</main>
      </div>
      {/* Po povýšení aplikace: jednou na uživatele a verzi, viz VersionNoticeDialog. */}
      <VersionNoticeDialog />
    </div>
  );
}
